import { EnrollmentModal, Pagination } from '@app/components'
import { useEnrollments } from '@app/lib'
import { type SugarEnrollment } from '@app/types'
import { ActionIcon, Box, createStyles, Group, LoadingOverlay, Table } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconEdit } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { map } from 'lodash'
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react'

const useStyles = createStyles(() => ({
  noTopBorder: {
    borderTopWidth: 0
  },
  fitWidth: {
    width: 1,
    whiteSpace: 'nowrap'
  }
}))

export interface EnrollmentsTableHandle {
  showAddModal: () => void
}

export interface EnrollmentsTableProps {
  schoolYearId: string | null
}

export const EnrollmentsTable = forwardRef<EnrollmentsTableHandle, EnrollmentsTableProps>(({ schoolYearId }, ref) => {
  const { classes } = useStyles()
  const [page, setPage] = useState(1)
  const { data: enrollments, isFetching: isFetchingEnrollments } = useEnrollments(
    page,
    schoolYearId ? { schoolYearId } : {}
  )
  const [adding, addModal] = useDisclosure(false)
  const [editing, setEditing] = useState<SugarEnrollment | null>(null)

  useImperativeHandle(ref, () => ({
    showAddModal: addModal.open
  }))

  return (
    <Box pos='relative'>
      <LoadingOverlay visible={isFetchingEnrollments} />
      <Table
        className={classes.noTopBorder}
        horizontalSpacing='xs'
        mb='xl'
        verticalSpacing='xs'
        withBorder
      >
        <thead>
          <tr>
            <th className={classes.fitWidth}>Child Payment ID</th>
            <th className={classes.fitWidth}>Name</th>
            <th className={classes.fitWidth}>Enrollment Start</th>
            <th className={classes.fitWidth}>Enrollment End</th>
            <th className={classes.fitWidth}>Provider</th>
            <th className={classes.fitWidth}>Status</th>
            <th className={classes.fitWidth}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {map(enrollments?.data, (enrollment, enrollmentIdx) => (
            <tr key={`enrollment-${enrollmentIdx}`}>
              <td className={classes.fitWidth}>{enrollment.studentChildPaymentId ?? '-'}</td>
              <td className={classes.fitWidth}>
                {enrollment.studentLastName}, {enrollment.studentFirstName}
              </td>
              <td className={classes.fitWidth}>
                {enrollment.enrollmentStartDate ? dayjs(enrollment.enrollmentStartDate).format('MMMM D, YYYY') : '-'}
              </td>
              <td className={classes.fitWidth}>
                {enrollment.enrollmentEndDate ? dayjs(enrollment.enrollmentEndDate).format('MMMM D, YYYY') : '-'}
              </td>
              <td className={classes.fitWidth}>{enrollment.providerName}</td>
              <td className={classes.fitWidth}>{enrollment.enrollmentStatus ?? '-'}</td>
              <td className={classes.fitWidth}>
                <Group
                  align='center'
                  noWrap
                  position='center'
                  spacing={0}
                >
                  <ActionIcon
                    color='navy'
                    onClick={() => setEditing(enrollment)}
                    radius='xl'
                    tabIndex={0}
                    title='Edit Enrollment'
                    variant='filled'
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                </Group>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <Pagination
        onChange={setPage}
        total={enrollments?.pagination?.totalPages || 1}
        value={page}
      />
      <EnrollmentModal
        enrollment={null}
        onClose={addModal.close}
        opened={adding}
      />
      <EnrollmentModal
        enrollment={editing}
        key={editing?.enrollmentId}
        opened={!!editing}
        onClose={useCallback(() => setEditing(null), [])}
      />
    </Box>
  )
})

EnrollmentsTable.displayName = 'EnrollmentsTable'
